import { createContext, useState, ReactNode } from 'react';
import usePlayground from '@/modules/playground/hooks/usePlayground.hook';
import getUserAgentInfo from '@/core/utils/analytics/get-user-agent-info';
import getReferrer from '@/core/utils/analytics/get-referrer';
import { ENUM_CARD_EVENT_METRIC } from '@link-in-bio/domain';
import getSessionInfo from '@/core/utils/analytics/get-session-info';
import { ENUM_EMAIL_COLLECTION_EVENT } from '@/modules/playground/enums/email-collection-event.enum';
import { getDateRange } from '@/core/utils/date/date-range';
import useTrackVisitor from '../hooks/useTrackVisitor.hook';
import useTrackCardMetric from '../hooks/useTrackCardMetric.hook';
import useTrackEmailCollection from '../hooks/useTrackEmailCollection.hook';
import getEmailSessionInfo from '@/core/utils/analytics/get-email-session-info';

type AnalyticsProviderProps = {
  children: ReactNode;
};

type DateRange = { start: string; end: string };

type FilterKey = 'source' | 'country' | 'device' | 'browser' | 'os';
type UpdateFilterFn = (key: FilterKey, value: string) => void;

export const AnalyticsContext = createContext<
  | {
      captureVisitorMetric: () => Promise<void>;
      captureCtaClick: () => Promise<void>;
      captureCardMetric: (cardId: string, event: ENUM_CARD_EVENT_METRIC) => Promise<void>;
      captureEmailCollectionMetric: (event: ENUM_EMAIL_COLLECTION_EVENT) => Promise<void>;
      dateFilter: DateRange;
      setDateFilter: (cardType: DateRange) => void;
      updateFilter: UpdateFilterFn;
      filters: {
        source: string;
        country: string;
        device: string;
        browser: string;
        os: string;
      };
    }
  | undefined
>(undefined);

function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { state } = usePlayground();
  const trackVisitor = useTrackVisitor();
  const trackCardMetric = useTrackCardMetric();
  const trackEmailCollection = useTrackEmailCollection();

  const initialDateRange = getDateRange();
  const [dateFilter, setDateFilter] = useState<DateRange>({
    start: initialDateRange.start.toISOString(),
    end: initialDateRange.end.toISOString(),
  });

  const [filters, setFilters] = useState<{
    source: string;
    country: string;
    device: string;
    browser: string;
    os: string;
  }>({
    source: '',
    country: '',
    device: '',
    browser: '',
    os: '',
  });

  const updateFilter = (key: keyof NonNullable<typeof filters>, value: string) => {
    setFilters((prev) => {
      const base = prev ?? {
        source: '',
        country: '',
        device: '',
        browser: '',
        os: '',
      };
      return {
        ...base,
        [key]: value,
      };
    });
  };

  const captureVisitorMetric = async () => {
    if (!state.payload._id || window.location.pathname === '/playground') return;

    const { isUnique } = getSessionInfo();
    const userAgentInfo = getUserAgentInfo();

    trackVisitor.mutate({
      link_in_bio_id: state.payload._id!,
      is_unique_visitor: isUnique,
      device: userAgentInfo.device,
      browser: userAgentInfo.browser,
      os: userAgentInfo.os,
      source: await getReferrer(),
    });
  };

  const captureCtaClick = async () => {
    if (!state.payload._id || window.location.pathname === '/playground') return;

    const { isUnique } = getSessionInfo();
    const userAgentInfo = getUserAgentInfo();

    trackVisitor.mutate({
      link_in_bio_id: state.payload._id!,
      is_unique_visitor: false,
      cta_button_analytics: {
        total_clicks: 1,
        ...(isUnique ? { unique_clicks: 1 } : {}),
        device: userAgentInfo.device,
        browser: userAgentInfo.browser,
        os: userAgentInfo.os,
        source: await getReferrer(),
      },
    });
  };

  const captureCardMetric = async (cardId: string, event: ENUM_CARD_EVENT_METRIC) => {
    if (!state.payload._id || window.location.pathname === '/playground') return;

    const { sessionId } = getSessionInfo();
    const userAgentInfo = getUserAgentInfo();

    trackCardMetric.mutate({
      link_in_bio_id: state.payload._id!,
      card_id: cardId,
      session_id: sessionId,
      event,
      device: userAgentInfo.device,
      browser: userAgentInfo.browser,
      os: userAgentInfo.os,
      source: await getReferrer(),
    });
  };

  const captureEmailCollectionMetric = async (event: ENUM_EMAIL_COLLECTION_EVENT) => {
    if (!state.payload._id || window.location.pathname === '/playground') return;

    const userAgentInfo = getUserAgentInfo();
    const { isUnique } = getEmailSessionInfo();

    trackEmailCollection.mutate({
      link_in_bio_id: state.payload._id!,
      event,
      device: userAgentInfo.device,
      browser: userAgentInfo.browser,
      os: userAgentInfo.os,
      source: await getReferrer(),
      ...(isUnique ? { unique_clicks: 1 } : {}),
    });
  };

  const value = {
    captureVisitorMetric,
    captureCtaClick,
    captureCardMetric,
    captureEmailCollectionMetric,
    dateFilter,
    setDateFilter,
    filters,
    updateFilter,
  };

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export default AnalyticsProvider;
